import {
  DRAFT_APPROVAL_KIND,
  isSessionDraftToolName,
  resolveDraftPresentation,
} from '../../contracts/approval-kinds';
import type { ChatItem, ToolUseItem } from '../../contracts/harness.types';
import type { ViewedDocumentContext } from '../../contracts/projects/requests.types';
import { sessionDraftBeforeText } from '../../contracts/session-draft-before-text';
import { getPendingApprovalIdForItem } from '../../data/harness/approvals/get-pending-approval-id';
import type {
  DraftApprovalBlockProps,
  ResolveDraftApproval,
} from './draft-approval-block';
import { readTextEditPreview } from './text-edit-preview';

export type SessionDraftCard = {
  props: Omit<DraftApprovalBlockProps, 'renderBody'>;
};

/**
 * The draft card for a timeline item, or `null` when the item is not a pending
 * draft approval.
 *
 * The kind comes from the snapshot once it loads, which is authoritative, else
 * from the live approval event, which carries it from the first frame. So a
 * `default` approval (a chat the backend gives no draft card) goes straight to
 * the plain approval card instead of showing a draft card first. Nothing is
 * painted while the tool input streams: until the approval arrives, nothing
 * says which card it gets. With neither kind, a session-write tool is a draft
 * by name; otherwise `toBlockStatus` maps pending_approval to running and the
 * row spins forever.
 */
export const sessionDraftCard = (
  item: ToolUseItem,
  {
    items,
    viewedDocument,
    onResolve,
  }: {
    items?: ChatItem[];
    /** The on-screen document, with the session it belongs to. */
    viewedDocument?: ViewedDocumentContext & { session_id?: string };
    onResolve?: ResolveDraftApproval;
  }
): SessionDraftCard | null => {
  const pendingApprovalId = getPendingApprovalIdForItem(item);
  const kind = item.approval?.approval_kind ?? item.pendingApprovalKind;
  const isPending =
    item.status === 'pending_approval' &&
    Boolean(pendingApprovalId) &&
    (kind === DRAFT_APPROVAL_KIND || (!kind && isSessionDraftToolName(item.name)));
  if (!isPending) return null;

  const toolInput = (item.input as Record<string, unknown> | undefined) ?? {};
  const draft = resolveDraftPresentation(item.name, item.approval?.context?.draft);
  // The hydrated approval can carry a context without a preview while the live
  // event already has one, so each source is read for a preview, not just a context.
  const serverPreview =
    readTextEditPreview(item.approval?.context, draft?.target) ??
    readTextEditPreview(item.pendingApprovalContext, draft?.target);
  return {
    props: {
      toolName: item.name,
      toolInput,
      partialInput: item.partialInput,
      status: 'pending_approval',
      approvalId: pendingApprovalId ?? undefined,
      approvalKind: kind,
      draft,
      // The server's text wins, so the chat isn't scanned for one.
      beforeText: serverPreview
        ? undefined
        : sessionDraftBeforeText(items, item, viewedDocument),
      serverPreview,
      onResolve,
    },
  };
};
